"use client";

import { faCode, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { apiClient, publicApiClient } from "@/api/axiosInstance";
import { API_ENDPOINTS, resolveEndpoint } from "@/api/endpoints";
import { useToast } from "@/components/common/Toast";
import { useEnvironmentStore } from "@/store/environments.store";
import {
	CustomEditFlowPayload,
	type IFlowListData,
	useFlowStore
} from "@/store/flow.store";
import { FlowAction, useFlowActionsStore } from "@/store/flowActions.store";
import { type ApiError, handleApiError } from "@/utils/errorHandler";
import CreateFlowPanel from "./CreateFlowPanel";
import EditFlowPanel from "./EditFlowPanel";

export interface FlowApiOptions {
	brandId: string;
	environmentId: string;
	type: string;
}

export interface FlowTarget {
	id: string;
	name: string;
	actions?: Array<{
		id: string;
		definition?: {
			id: string;
			name: string;
		};
		status: "ENABLED" | "DISABLED";
	}>;
}
export interface FlowDefinition {
	id: string;
	name: string;
	description?: string;
}

export interface FlowCredential {
	[key: string]: string;
}

export interface CreateFlowProps {
	flowTypeId: string;
	flowTargetId: string;
	onCreateFlow: (data: {
		credential: Record<string, string>;
		flowDefinitions: Array<{ id: string }>;
		logo: string;
	}) => void;
	flowDefinitions: Array<{
		id: string;
		description: string;
		flowActionId: string;
	}>;
	flowActions: FlowAction[];
	isLoading?: boolean;
}

export interface EditFlowProps {
	flowTargetId: string;
	onUpdateFlow: (data: {
		credential: Record<string, string> | null;
		flows: Array<{ id: string; status: "ENABLED" | "DISABLED" }>;
		flowDefinitions: Array<{ id: string }>;
		logo: string;
	}) => void;
	onDeleteFlow: (flowId: string) => void;
	flows: Array<CustomEditFlowPayload>;
	isLoading?: boolean;
}

const FLOW_DEFINITION_QUERY_KEY = (brandId: string, flowTargetId: string) =>
	["brand-flow-definition", brandId, flowTargetId] as const;

const FLOW_ACTIONS_QUERY_KEY = (brandId: string, flowTypeId: string) =>
	["brand-flow-actions", brandId, flowTypeId] as const;

export const getFlows = async ({
	brandId,
	environmentId,
	type
}: FlowApiOptions): Promise<IFlowListData[]> => {
	const headers: Record<string, string> = {
		"X-Brand-Id": brandId,
		Authorization: environmentId
	};

	const params: Record<string, string> = {};
	if (type) {
		params.type = type;
	}

	try {
		const response = await publicApiClient.get(
			resolveEndpoint(API_ENDPOINTS, ["FLOWS", "LIST"]),
			{
				headers,
				params
			}
		);

		if (response.data && typeof response.data === "object") {
			if (Array.isArray(response.data)) {
				return response.data;
			}

			if (response.data.name && Array.isArray(response.data.targets)) {
				return [response.data];
			}
		}

		return [];
	} catch (error) {
		handleApiError(error as ApiError, "Failed to fetching flows");
		throw error;
	}
};

export const createFlow = async (data: {
	brandId: string;
	environmentId: string;
	credential: Record<string, string>;
	logo: string;
	flowDefinitions: Array<{ id: string }>;
}) => {
	const { brandId, environmentId, ...requestData } = data;

	const response = await publicApiClient.post(
		resolveEndpoint(API_ENDPOINTS, ["FLOWS", "CREATE"]),
		requestData,
		{
			headers: {
				"X-Brand-Id": brandId,
				Authorization: environmentId,
				"Content-Type": "application/json"
			}
		}
	);

	return response.data;
};

export const updateFlow = async (data: {
	brandId: string;
	environmentId: string;
	credential: Record<string, string> | null;
	flows: Array<{ id: string; status: "ENABLED" | "DISABLED" }>;
	flowDefinitions: Array<{ id: string }>;
	logo: string;
}) => {
	const { brandId, environmentId, ...requestData } = data;

	const response = await publicApiClient.put(
		resolveEndpoint(API_ENDPOINTS, ["FLOWS", "UPDATE"]),
		requestData,
		{
			headers: {
				"X-Brand-Id": brandId,
				Authorization: environmentId,
				"Content-Type": "application/json"
			}
		}
	);

	return response.data;
};

export const deleteFlow = async (data: {
	brandId: string;
	environmentId: string;
	flowId: string;
}) => {
	const { brandId, environmentId, flowId } = data;

	const response = await publicApiClient.delete(
		resolveEndpoint(API_ENDPOINTS, ["FLOWS", "DELETE_BY_ID"], flowId),
		{
			headers: {
				"X-Brand-Id": brandId,
				Authorization: environmentId
			}
		}
	);

	return response.data;
};

const extractFlowData = (targets: IFlowListData[], flowTargetId: string) => {
	const flows: CustomEditFlowPayload[] = [];

	const target = targets[0]?.targets.find(
		(target) => target.id === flowTargetId
	);

	if (!target) return flows;

	target.actions.forEach((action) => {
		if (!action.definition) return;

		const { definition } = action;

		flows.push({
			flowTargetId,
			flowId: definition?.flow?.id,
			flowStatus: definition?.flow?.status,
			flowDefinitionId: definition.id,
			flowDefinitionDescription: definition.description,
			targetName: target.name,
			logo: target.logo,
			actionName: action.name
		});
	});

	return flows;
};

const ViewFlow: React.FC<{
	brandId: string;
	flowTypeId: string;
	flowTargetId: string;
	setIsPanelOpen: (value: boolean) => void;
}> = ({ brandId, flowTypeId, flowTargetId, setIsPanelOpen }) => {
	const queryClient = useQueryClient();
	const { success } = useToast();
	const { selectedEnvironmentSecret } = useEnvironmentStore();
	const { selectedFlowTypeName, flows, setFlows, setIsLoading, setError } =
		useFlowStore();
	const { flowActions, setFlowActions } = useFlowActionsStore();

	const [mode, setMode] = useState<"edit" | "create" | "none">("none");

	useEffect(() => {
		if (flowTypeId) {
			void (async () => {
				try {
					const { data } = await apiClient.get(
						resolveEndpoint(API_ENDPOINTS, ["FLOW_ACTIONS", "LIST"], flowTypeId)
					);

					setFlowActions(data);

					void queryClient.prefetchQuery({
						queryKey: FLOW_ACTIONS_QUERY_KEY(brandId, flowTypeId),
						queryFn: async () => data,
						staleTime: 5 * 60 * 1000
					});
				} catch (error) {
					console.error("Error fetching flow actions:", error);
				}
			})();
		}
	}, [flowTypeId, brandId, queryClient, setFlowActions]);

	const {
		data: currentDefinition,
		isLoading: isLoadingDefinition,
		isError: isDefinitionError,
		error: definitionError
	} = useQuery({
		queryKey: FLOW_DEFINITION_QUERY_KEY(brandId || "", flowTargetId || ""),
		queryFn: async () => {
			const { data } = await apiClient.get(
				resolveEndpoint(API_ENDPOINTS, ["FLOW_DEFINITIONS", "LIST"]),
				{
					params: {
						flowTargetId
					}
				}
			);

			return data;
		},
		enabled: !!flowTargetId
	});

	const createFlowMutation = useMutation({
		mutationFn: createFlow,
		onSuccess: () => {
			success("Flow created successfully");
			queryClient.invalidateQueries({ queryKey: ["flows", brandId] });
			setIsPanelOpen(false);
		},
		onError: (error) => {
			handleApiError(error as ApiError, "Failed to create flow");
		}
	});

	const updateFlowMutation = useMutation({
		mutationFn: updateFlow,
		onSuccess: () => {
			success("Flow updated successfully");
			queryClient.invalidateQueries({ queryKey: ["flows", brandId] });
			setIsPanelOpen(false);
		},
		onError: (error) => {
			handleApiError(error as ApiError, "Failed to update flow");
		}
	});

	const deleteFlowMutation = useMutation({
		mutationFn: deleteFlow,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["flows", brandId] });
			setIsPanelOpen(false);
		},
		onError: (error) => {
			handleApiError(error as ApiError, "Failed to delete flow");
		}
	});

	const handleCreateFlow = (data: {
		credential: Record<string, string>;
		flowDefinitions: Array<{ id: string }>;
		logo: string;
	}) => {
		createFlowMutation.mutate({
			brandId: brandId || "",
			environmentId: selectedEnvironmentSecret || "",
			...data
		});
	};

	const handleUpdateFlow = (data: {
		credential: Record<string, string> | null;
		flows: Array<{ id: string; status: "ENABLED" | "DISABLED" }>;
		flowDefinitions: Array<{ id: string }>;
		logo: string;
	}) => {
		updateFlowMutation.mutate({
			brandId: brandId || "",
			environmentId: selectedEnvironmentSecret || "",
			...data
		});
	};

	const handleDeleteFlow = (flowId: string) => {
		deleteFlowMutation.mutate({
			brandId: brandId || "",
			environmentId: selectedEnvironmentSecret || "",
			flowId
		});
	};

	const currentTarget = useMemo(() => {
		return flows
			.flatMap((flow) => flow.targets ?? [])
			.find((t) => t.id === flowTargetId);
	}, [flows, flowTargetId]);

	const currentFlows = useMemo(() => {
		if (!currentTarget?.actions) return [];

		return currentTarget.actions
			.filter((action) => action.definition)
			.map((action) => ({
				id: action.id
			}));
	}, [currentTarget]);

	useEffect(() => {
		const fetchFlows = async () => {
			if (!selectedEnvironmentSecret || !selectedFlowTypeName) {
				return;
			}

			try {
				setIsLoading(true);
				setError(null);

				const data = await getFlows({
					brandId: brandId as string,
					environmentId: selectedEnvironmentSecret as string,
					type: selectedFlowTypeName as string
				});

				const newFlowsData = JSON.parse(JSON.stringify(data));

				setFlows(newFlowsData);
			} catch (error) {
				setError(error as Error);
				handleApiError(error as ApiError, "Failed to load flows");
			} finally {
				setIsLoading(false);
			}
		};

		fetchFlows();
	}, [
		brandId,
		selectedEnvironmentSecret,
		selectedFlowTypeName,
		setFlows,
		setIsLoading,
		setError
	]);

	useEffect(() => {
		if (isLoadingDefinition) return;

		if (!currentTarget && !currentDefinition?.length) {
			setMode("none");
			return;
		}

		const hasActiveFlows = currentFlows.length > 0;

		if (hasActiveFlows) {
			setMode("edit");
		} else {
			setMode("create");
		}
	}, [
		currentTarget,
		currentDefinition,
		isLoadingDefinition,
		currentFlows.length
	]);

	if (isLoadingDefinition) {
		return (
			<div className="flex justify-center items-center h-screen">
				<FontAwesomeIcon
					icon={faSpinner}
					spin
					className="h-8 w-8 text-blue-500"
				/>
			</div>
		);
	}

	if (isDefinitionError) {
		handleApiError(
			definitionError as ApiError,
			"Failed to load flow definition"
		);
		return (
			<div className="p-4 text-red-600">Error loading flow definition</div>
		);
	}

	const flowsData = extractFlowData(flows, flowTargetId);

	switch (mode) {
		case "create":
			return (
				<CreateFlowPanel
					flowTypeId={flowTypeId}
					flowTargetId={flowTargetId}
					onCreateFlow={handleCreateFlow}
					flowDefinitions={currentDefinition.map(
						(def: {
							id: string;
							description: string;
							flowActionId: string;
						}) => ({
							id: def.id,
							description: def.description,
							flowActionId: def.flowActionId
						})
					)}
					flowActions={flowActions}
					isLoading={createFlowMutation.isPending}
				/>
			);

		case "edit":
			return (
				<EditFlowPanel
					flowTargetId={flowTargetId}
					flows={flowsData}
					onUpdateFlow={handleUpdateFlow}
					onDeleteFlow={handleDeleteFlow}
					isLoading={updateFlowMutation.isPending}
				/>
			);

		default:
			return (
				<div className="flex flex-col items-center justify-center p-8 h-full">
					<div className="max-w-md bg-white rounded-lg shadow-md overflow-hidden">
						<div className="bg-gray-50 p-6 text-center border-b border-gray-200">
							<div className="relative w-24 h-24 mx-auto mb-4">
								<div className="absolute inset-0 bg-red-100 rounded-full opacity-20 animate-pulse" />
								<div className="absolute inset-0 flex items-center justify-center">
									<FontAwesomeIcon icon={faCode} />
								</div>
							</div>
							<h2 className="text-xl font-semibold text-gray-800 mb-1">
								No Flow Definition Found
							</h2>
							<p className="text-gray-500">
								There is no flow definition mapped to this flow target.
							</p>
						</div>
					</div>
				</div>
			);
	}
};

export default ViewFlow;
